import {
    takeEvery,
    all,
    put,
    select,
    call,
    fork
} from 'redux-saga/effects';
import fp from 'lodash/fp';
import { push, replace, LOCATION_CHANGE } from 'connected-react-router';
import urlJoin from 'url-join';
import queryString from 'query-string';
import moment from 'moment';

import actionFactory from '../../webcommon/header/filterheader/actions';
import * as filterHeaderSelectors from '../../webcommon/header/filterheader/selectors';

import * as actions from './actions';
import * as csaImpactApi from '../../api/csaImpactApi';
import * as selectors from './selectors';
import * as tradesPricedTodayActions from '../impactsummary/tradepricedtoday/actions';
import * as imImpactSummaryActions from '../impactsummary/imimpactsummary/actions';
import * as impactDetailedActions from '../impactdetailed/actions';
import * as pricingParametersActions from '../pricingparameters/actions';
import { searchToFilters, filtersToSearch } from '../../common/urlUtils';
import * as pricingParametersApi from '../../api/pricingParamsApi';
import * as appSelectors from '../../app/selectors';
import * as routerSelectors from '../../common/router/selectors';
import * as configurationApi from '../../api/configurationApi';
import * as appAdapters from '../../api/adapters/systemInfoAdapter';
import { getVirtualDirectory } from '../../common/helpermethods/HelperMethods';
import * as systemInfoApi from '../../api/systemInfoApi';
import { restructureListByType } from '../../api/adapters/filterAdapters';
import { mapCsaSummariesToGrid } from '../../api/adapters/csaImpactAdapter';
import * as appConstants from '../../common/constants';
import * as appActions from '../../app/actions';

export const PAGE_ROUTE = urlJoin(getVirtualDirectory(), '/impactanalysis');
const filterHeaderActions = actionFactory('ImpactAnalysis');

export function* loadTradesPricedToday(tradeInfoList) {
    yield put(tradesPricedTodayActions.setTradesPricedToday(tradeInfoList));
}

export function* loadImpactSummary(csaSummaries) {
    yield put(imImpactSummaryActions.setImpactSummaryRows(csaSummaries));
    yield put(imImpactSummaryActions.setLoading(false));
}

export function* loadImpactDetailed(drildowns) {
    yield put(impactDetailedActions.setImpactDetailedRows(drildowns));
    yield put(impactDetailedActions.setLoading(false));
}

export function* setAvailableCsasByCounterparty(counterparties = []) {
    const potentialCsaValues = yield (select(appSelectors.getCsas));
    const allowedCsas = counterparties && counterparties.length
        ? potentialCsaValues.filter(csa => counterparties.includes(csa.counterparty))
        : potentialCsaValues;

    const restructeredCsas = restructureListByType(allowedCsas);

    yield put(filterHeaderActions.setAvailableListValues('csa', restructeredCsas));

    const activeFilters = yield select(filterHeaderSelectors.getFilters, 'ImpactAnalysis');
    const csaFilterIndex = fp.findIndex(filter => filter.definition.id === 'csa', activeFilters);
    const csaFilter = activeFilters[csaFilterIndex];

    if ((Array.isArray(csaFilter.value) && csaFilter.value.length) || allowedCsas.length === 1) {
        let newFilterValue;
        if (allowedCsas.length === 1) {
            newFilterValue = [allowedCsas[0].value];
        } else {
            newFilterValue = csaFilter
                .value.filter(csaId => allowedCsas.find(csa => csa.value === csaId));
        }

        const newFilters = fp.set(`[${csaFilterIndex}].value`, newFilterValue, activeFilters);
        const openFilter = yield select(filterHeaderSelectors.getCurrentlyOpenFilter, 'ImpactAnalysis');
        yield put(filterHeaderActions.setFilters(newFilters));
        if (openFilter) {
            yield put(filterHeaderActions.openFilter(openFilter));
        }
    }
}

export function* filterChanged(forceUpdate, openCsaFilter = false) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        const currentlyOpenFilter = yield (select(filterHeaderSelectors.getCurrentlyOpenFilter, 'ImpactAnalysis'));
        if (!currentlyOpenFilter) {
            const filters = yield select(filterHeaderSelectors.getFilters, 'ImpactAnalysis');
            const filterSearch = filtersToSearch(filters);
            const sp = queryString.parse(filterSearch);
            let force = false;
            if (forceUpdate) {
                sp.force = 'true';
                force = true;
            }
            if (openCsaFilter) {
                sp.openFilter = 'csa';
            }
            const newSearch = `?${queryString.stringify(sp)}`;
            const currentSearch = yield select(routerSelectors.getSearch);
            if (force || !currentSearch) {
                yield put(replace(`${PAGE_ROUTE}${newSearch}`));
            } else if (currentSearch !== newSearch) {
                yield put(push(`${PAGE_ROUTE}${newSearch}`));
            }
        }
    }
}

export function* getCsas() {
    const filters = yield select(filterHeaderSelectors.getFilters, 'ImpactAnalysis');
    const counterpartyFilter = filters.find(filter => filter.definition.id === 'counterparty');
    yield call(setAvailableCsasByCounterparty, counterpartyFilter.value);
}

export function* getCounterparties() {
    const counterparties = yield select(appSelectors.getCounterparties);
    const restructuredCounterparties = restructureListByType(counterparties);
    yield put(filterHeaderActions.setAvailableListValues('counterparty', restructuredCounterparties));
}

export function* getAvailableValues(action) {
    switch (action.filterId) {
        case 'csa':
            yield call(getCsas);
            break;
        case 'counterparty':
            yield call(getCounterparties);
            break;
        default:
    }
}

export function* handleUpdate(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        let openCsaFilter = false;
        if (action.filterId === 'runDate') {
            try {
                const EodDate = moment(action.value.from).format(appConstants.dateFormat);
                const csas = yield call(systemInfoApi.csaListByEod, EodDate);
                const csaList = appAdapters.createCsaList(csas);
                yield put(appActions.fetchCsaDataSucceeded(csaList));
                if (!csaList.length) {
                    yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
                }
                yield call(getCounterparties);
                yield call(getCsas);
            } catch (error) {
                yield put(appActions.fetchCsaDataFailed(error.message));
            }
        }
        if (action.filterId === 'counterparty') {
            yield call(setAvailableCsasByCounterparty, action.value);
            if (action.type === filterHeaderActions.UPDATE_FILTER) {
                const potentialCsaValues = yield (select(appSelectors.getCsas));
                const newCounterparties = action.value || [];
                const allowedCsas = newCounterparties.length
                    ? potentialCsaValues.filter(csa => newCounterparties.includes(csa.counterparty))
                    : potentialCsaValues;
                if (allowedCsas.length !== 1) {
                    openCsaFilter = true;
                }
            }
        } else if (action.filterId === 'csa' && Array.isArray(action.value) && action.value.length) {
            const csas = yield select(appSelectors.getCsas);
            const csa = fp.find(
                csa => csa.value === action.value[0],
                csas
            );
            yield call(setAvailableCsasByCounterparty, csa.counterparty);

            const activeFilters = yield select(filterHeaderSelectors.getFilters, 'ImpactAnalysis');
            const counterpartyFilterIndex = fp.findIndex(filter => filter.definition.id === 'counterparty', activeFilters);
            const newFilters = csa.counterparty ? fp.set(`[${counterpartyFilterIndex}].value`, [csa.counterparty], activeFilters) : activeFilters;
            yield put(filterHeaderActions.setFilters(newFilters));
        } else if (action.type === filterHeaderActions.RESET_FILTERS) {
            const filters = yield select(filterHeaderSelectors.getFilters, 'ImpactAnalysis');
            const EodDate = filters[0].value.from;
            const csas = yield call(systemInfoApi.csaListByEod, EodDate);
            const csaList = appAdapters.createCsaList(csas);
            yield put(appActions.fetchCsaDataSucceeded(csaList));
            if (!csaList.length) {
                yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
            }
            const counterpartyFilterValue = fp.flow(
                fp.find(filter => fp.get('definition.id', filter) === 'counterparty'),
                fp.get('value')
            )(filters);
            yield call(setAvailableCsasByCounterparty, counterpartyFilterValue);
        }

        yield call(filterChanged, false, openCsaFilter);
    }
}

export function* loadDefaultConfiguration(api) {
    try {
        const config = yield call(api.getConfiguration, 'ImpactAnalysis');
        if (config) {
            yield put(actions.setDefaultConfig(config.currency,
                config.showCsaSummary,
                config.showDefaultMappings));
            if (config.impactDetailedColumns) {
                yield put(imImpactSummaryActions.setDefaultConfig(config.impactSummaryColumns));
            }
            if (config.impactDetailedColumns) {
                yield put(impactDetailedActions.setDefaultConfig(config.impactDetailedColumns));
            }
        }
    } catch (error) {
        yield put(actions.fetchDefaultConfigFailed(error.message));
    }
}

export function* impactAnalysisLoaded(configApi) {
    try {
        const initialised = yield (select(selectors.getInitialised));
        if (!initialised) {
            yield fork(loadDefaultConfiguration, configApi);
            yield put(actions.setLoading(true));
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            yield put(filterHeaderActions.setFilterDefinitions(filterDefinitions));
            const defaultFilters = yield select(selectors.getDefaultFilters);
            const availableEOD = yield select(appSelectors.getAvailableEOD);
            const singleDate = fp.set('to', null)(availableEOD);
            const filters = fp.set('[0].value', singleDate)(defaultFilters);
            const csas = yield call(systemInfoApi.csaListByEod, availableEOD.from);
            const csaList = appAdapters.createCsaList(csas);
            yield put(appActions.fetchCsaDataSucceeded(csaList));
            if (!csaList.length) {
                yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
            }
            yield put(filterHeaderActions.setDefaultFilters(filters));
            const search = yield select(routerSelectors.getSearch);
            if (!search) {
                yield put(filterHeaderActions.resetFilters());
            } else {
                const filters = searchToFilters(search, filterDefinitions, defaultFilters);
                yield put(filterHeaderActions.setFilters(filters));
            }
            yield call(getCounterparties);
            yield put(actions.setInitialised(true));
            yield call(filterChanged, true);
        }
    } finally {
        yield put(actions.setLoading(false));
    }
}

export function* handleLocationChangedWithoutQuerystring() {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        yield call(filterChanged, false);
    }
}

export function* loadRowMappings(api) {
    try {
        yield put(pricingParametersActions.setLoading(true));
        const date = yield select(selectors.getDateFrom);
        const activeCsa = yield select(selectors.getCsas);
        if (activeCsa && activeCsa.length) {
            const pricingParametersMappings = yield call(api.pricingParamsMetaData,
                moment(date).format(appConstants.dateFormat), activeCsa[0]);
            yield put(pricingParametersActions.setRowMappings(pricingParametersMappings));
        }
    } catch (error) {
        yield put(pricingParametersActions.fetchPricingParametersMappingsFailed(error.message));
    } finally {
        yield put(pricingParametersActions.setLoading(false));
    }
}

export function* handleLocationChanged(api, action) {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        try {
            const filterDefinitions = yield select(selectors.getFilterDefinitions);
            const defaultFilters = yield select(selectors.getDefaultFilters);
            const filters = searchToFilters(
                action.payload.location.search,
                filterDefinitions,
                defaultFilters
            );
            yield put(filterHeaderActions.setFilters(filters));
            const params = queryString.parse(action.payload.location.search);
            const force = params.force === 'true';

            const requestedServerFilters = fp.flow(
                fp.filter(filter => filter.value && filter.definition.server),
                fp.filter(filter => filter.definition.id !== 'counterparty'),
                fp.sortBy(filter => filter.definition.id),
                JSON.stringify
            )(filters);
            const currentServerFilters = yield select(selectors.getCurrentServerFilters);
            if (force || (currentServerFilters !== requestedServerFilters)) {
                const dateFrom = yield select(selectors.getDateFrom);
                const dateToday = yield select(selectors.getDateIsToday);
                const csas = yield select(selectors.getCsas);
                if ((dateFrom !== null || dateToday) && csas.length !== 0 && action.filterId !== 'counterparty') {
                    yield fork(loadRowMappings, pricingParametersApi);
                    const csaSummaryInfo = yield call(api.csaImpactInformation, filters);
                    const csas = yield select(appSelectors.getCsas);
                    csaSummaryInfo.csaSummaries = fp.map((csaSummaryRow) => {
                        const csaValue = csaSummaryRow.csa;
                        const csaDetail = fp.find(csa => csa.value === csaValue)(csas);
                        if (csaDetail) {
                            return fp.set('counterparty', csaDetail.counterparty, csaSummaryRow);
                        }
                        return csaSummaryRow;
                    }, csaSummaryInfo.csaSummaries);
                    const summaryRows = mapCsaSummariesToGrid(csaSummaryInfo.csaSummaries);
                    const csaSummaryRows = summaryRows.map(summaryRow => (summaryRow.title ? fp.find(csa => csa.title === summaryRow.title)(csas) : null));
                    const activeFilters = fp.compact(csaSummaryRows);
                    const activeCounterparties = fp.uniq(activeFilters.map(fp.get('counterparty')));
                    const activeCsas = activeFilters.map(fp.get('title'));
                    yield put(imImpactSummaryActions.setSavedFilters(activeCounterparties, activeCsas));
                    csaSummaryInfo.csaDrilldowns = fp.map((csaDrilldownRow) => {
                        if (csaDrilldownRow.titleRow) {
                            const csaId = csaDrilldownRow.imModel;
                            const csaDetail = fp.find(csa => csa.value === csaId, csas);
                            if (csaDetail) {
                                return fp.set('counterparty', csaDetail.counterparty, csaDrilldownRow);
                            }
                        }

                        return csaDrilldownRow;
                    }, csaSummaryInfo.csaDrilldowns);

                    yield fork(loadTradesPricedToday, csaSummaryInfo.tradeInfoList);
                    yield fork(loadImpactSummary, csaSummaryInfo.csaSummaries);
                    yield fork(loadImpactDetailed, csaSummaryInfo.csaDrilldowns);
                    yield put(actions.setCurrentCsaStatus(fp.get('csaSummaries[0].monitorStatus')(csaSummaryInfo)));
                    yield put(actions.setCurrentCsaType(fp.get('csaSummaries[0].csaType')(csaSummaryInfo)));
                    yield put(actions.setTransactionId(fp.get('impactAnalysisId')(csaSummaryInfo)));

                    yield put(actions.setLoading(true));
                    yield put(imImpactSummaryActions.setLoading(true));
                    yield put(impactDetailedActions.setLoading(true));
                    const {
                        dealTypeModelMapping,
                        ...data
                    } = yield call(api.csaImpacts, filters);
                    yield put(actions.setSummaryInfoStats(data, requestedServerFilters));
                    yield put(actions.setSummaryGridRows(dealTypeModelMapping));
                    yield put(actions.setSummaryInfoLoaded(true));
                    yield put(pricingParametersActions.removeAllRows());
                    yield put(pricingParametersActions.addPricingParametersRow());
                    yield put(pricingParametersActions.setPricingParametersInfoLoaded(true));


                    yield put(imImpactSummaryActions.setImpactSummaryLoaded(true));

                    yield put(impactDetailedActions.setImpactDetailedDataLoaded(true));
                } else {
                    yield put(actions.setSummaryInfoLoaded(false));
                    yield put(pricingParametersActions.setPricingParametersInfoLoaded(false));
                    yield put(imImpactSummaryActions.setImpactSummaryLoaded(false));
                    yield put(impactDetailedActions.setImpactDetailedDataLoaded(false));
                }
            } else {
                const dateFrom = yield select(selectors.getDateFrom);
                const dateToday = yield select(selectors.getDateIsToday);
                const csas = yield select(selectors.getCsas);
                // csa changed to null, then back to the previous value, then the server filters
                // won't have changed and we end up here. However, we have data in the reducer
                // so we want to show the page.
                const loaded = ((dateFrom !== null || dateToday) && csas.length !== 0);
                yield put(actions.setSummaryInfoLoaded(loaded));
                yield put(pricingParametersActions.setPricingParametersInfoLoaded(loaded));
                yield put(imImpactSummaryActions.setImpactSummaryLoaded(loaded));
                yield put(impactDetailedActions.setImpactDetailedDataLoaded(loaded));
            }

            if (action && action.payload && action.payload.action === 'PUSH') {
                if (params.openFilter === 'csa') {
                    yield put(filterHeaderActions.openFilter('csa'));
                }
            }
        } catch (error) {
            yield put(actions.fetchSummaryInfoStatsFailed(error.message));
        } finally {
            yield put(impactDetailedActions.setLoading(false));
            yield put(imImpactSummaryActions.setLoading(false));
            yield put(actions.setLoading(false));
        }
    }
}

export function* openFilter(filter) {
    if (!filter.filterId) {
        yield call(filterChanged);
    }
}

const testLocation = emptySearch => (action) => {
    if (action.type !== LOCATION_CHANGE) {
        return false;
    }
    if (action.payload.location.pathname.toLowerCase() === PAGE_ROUTE.toLowerCase()) {
        return !!action.payload.location.search !== emptySearch;
    }
    return false;
};

export default function* rootSaga() {
    yield all([
        takeEvery(actions.IMPACT_ANALYSIS_LOADED, impactAnalysisLoaded, configurationApi),
        takeEvery([
            filterHeaderActions.UPDATE_FILTER,
            filterHeaderActions.REMOVE_FILTER,
            filterHeaderActions.RESET_FILTERS
        ], handleUpdate),
        takeEvery(
            filterHeaderActions.FETCH_AVAILABLE_LIST_VALUES,
            getAvailableValues
        ),
        takeEvery(
            testLocation(false),
            handleLocationChanged,
            csaImpactApi
        ),
        takeEvery(
            testLocation(true),
            handleLocationChangedWithoutQuerystring,
            csaImpactApi
        ),
        takeEvery(filterHeaderActions.OPEN_FILTER, openFilter)
    ]);
}
